import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, User, ChevronDown, Search, Users, Settings, FileText, Heart } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useNotifications } from '../../hooks/useNotifications';
import { CommunityGuidelinesModal } from '../UI';
import styles from './Header.module.css';

const Header: React.FC = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [showProfile, setShowProfile] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  const {
    notifications,
    unreadCount,
    showNotifications,
    setShowNotifications,
    handleMarkAllRead,
    handleNotificationClick,
    formatTime,
  } = useNotifications();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setShowProfile(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setShowNotifications]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const formatRole = (role: string) => {
    if (role === 'superadmin') return 'Super Admin';
    if (role === 'manager') return 'Mentor';
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  return (
    <header className={styles.header}>
      <div className={styles.searchContainer}>
        <Search size={16} className={styles.searchIcon} />
        <input type="text" placeholder="Search people, ripples, events..." className={styles.searchInput} />
      </div>

      <div className={styles.actions}>
        <button className={styles.sendRippleBtn} onClick={() => navigate('/appreciate')}>
          <Heart size={14} fill="#fff" strokeWidth={0} />
          Send a ripple
        </button>
        <div className={styles.notificationWrapper} ref={notifRef}>
          <button
            className={styles.notifButton}
            onClick={() => setShowNotifications(!showNotifications)}
          >
            <span className={styles.notifIcon} />
            {unreadCount > 0 && (
              <span className={styles.notifDot} />
            )}
          </button>

          {showNotifications && (
            <div className={styles.dropdown}>
              <div className={styles.dropdownHeader}>
                <span>Notifications</span>
                {unreadCount > 0 && (
                  <button onClick={handleMarkAllRead} className={styles.markAllRead}>
                    Mark all read
                  </button>
                )}
              </div>
              <div className={styles.notificationList}>
                {notifications.length === 0 ? (
                  <div className={styles.emptyNotifications}>
                    No notifications yet
                  </div>
                ) : (
                  notifications.slice(0, 5).map((notif) => (
                    <div
                      key={notif.id}
                      className={`${styles.notificationItem} ${!notif.is_read ? styles.unread : ''} ${notif.reference_type ? styles.clickable : ''}`}
                      onClick={() => notif.reference_type && handleNotificationClick(notif)}
                    >
                      <div className={styles.notificationContent}>
                        <span className={styles.notificationTitle}>{notif.title}</span>
                        {notif.message && (
                          <span className={styles.notificationMessage}>{notif.message}</span>
                        )}
                        <span className={styles.notificationTime}>
                          {formatTime(notif.created_at)}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <div className={styles.profileWrapper} ref={profileRef}>
          <button
            className={styles.profileButton}
            onClick={() => setShowProfile(!showProfile)}
          >
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="" className={styles.avatar} />
            ) : (
              <div className={styles.avatarPlaceholder}>
                {user && getInitials(user.first_name, user.last_name)}
              </div>
            )}
            <span className={styles.userName}>
              {user?.first_name} {user?.last_name}
            </span>
            <ChevronDown size={16} />
          </button>

          {showProfile && (
            <div className={styles.dropdown}>
              <div className={styles.profileInfo}>
                <div className={styles.profileDetails}>
                  <span className={styles.profileName}>
                    {user?.first_name} {user?.last_name}
                  </span>
                  <span className={styles.profileEmail}>{user?.email}</span>
                  <span className={styles.profileRole}>{user?.role ? formatRole(user.role) : ''}</span>
                </div>
              </div>
              <div className={styles.dropdownDivider} />
              <button
                className={styles.dropdownItem}
                onClick={() => {
                  setShowProfile(false);
                  navigate('/profile');
                }}
              >
                <User size={16} />
                <span>My Profile</span>
              </button>
              {(user?.role === 'superadmin' || user?.role === 'admin' || user?.role === 'executive' || user?.role === 'mentor') && (
                <button
                  className={styles.dropdownItem}
                  onClick={() => {
                    setShowProfile(false);
                    navigate('/team');
                  }}
                >
                  <Users size={16} />
                  <span>My Mentees</span>
                </button>
              )}
              {(user?.role === 'superadmin' || user?.role === 'admin') && (
                <button
                  className={styles.dropdownItem}
                  onClick={() => {
                    setShowProfile(false);
                    navigate('/settings');
                  }}
                >
                  <Settings size={16} />
                  <span>Settings</span>
                </button>
              )}
              <button
                className={styles.dropdownItem}
                onClick={() => {
                  setShowProfile(false);
                  setShowGuidelines(true);
                }}
              >
                <FileText size={16} />
                <span>Matasma Collective Principles</span>
              </button>
              <div className={styles.dropdownDivider} />
              <button className={styles.dropdownItem} onClick={handleLogout}>
                <LogOut size={16} />
                <span>Sign Out</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <CommunityGuidelinesModal
        isOpen={showGuidelines}
        onClose={() => setShowGuidelines(false)}
        readOnly
      />
    </header>
  );
};

export default Header;
